"use client";

import { Archive, ArrowRight, Beef, Fish, Leaf, Milk, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { NomboxLogo } from "@/components/shared/NomboxLogo";
import type { TutorialSlide } from "@/components/tutorial/TutorialSheet";
import { TutorialSheet } from "@/components/tutorial/TutorialSheet";

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TutorialModal({ isOpen, onClose }: TutorialModalProps) {
  const t = useTranslations("tutorial");

  const slides = useMemo(() => {
    const allSlides: TutorialSlide[] = [
      {
        title: t("welcome"),
        description: t("welcomeDescription"),
        visual: (
          <div className="flex items-center justify-center h-full">
            <NomboxLogo className="size-24 sm:size-28 text-text-secondary" />
          </div>
        ),
      },
      {
        title: t("addIngredients"),
        description: t("addIngredientsDescription"),
        visual: (
          <div className="bg-background rounded-lg border-2 border-input p-3">
            <div className="text-muted-foreground mb-1">{t("typeAndPressEnter")}</div>
            <div className="text-base">
              <div>{t("chickenBreast")}</div>
              <div>{t("milk")}</div>
              <div>{t("salmon")}</div>
              <div>{t("broccoli")}</div>
              <span className="animate-pulse ml-0.5">|</span>
            </div>
          </div>
        ),
      },
      {
        title: t("smartCategorization"),
        description: t("smartCategorizationDescription"),
        visual: (
          <div className="space-y-2">
            <div className="flex items-center gap-3 bg-category-meat-bg rounded-lg p-3 border">
              <Beef className="w-5 h-5 text-category-meat-text" />
              <span>{t("chickenBreastCapitalized")}</span>
              <span className="text-sm text-muted-foreground ms-auto">{t("categoryMeat")}</span>
            </div>
            <div className="flex items-center gap-3 bg-category-seafood-bg rounded-lg p-3 border">
              <Fish className="w-5 h-5 text-category-seafood-text" />
              <span>{t("salmonWithQuantity")}</span>
              <span className="text-sm text-muted-foreground ms-auto">{t("categorySeafood")}</span>
            </div>
            <div className="flex items-center gap-3 bg-category-produce-bg rounded-lg p-3 border">
              <Leaf className="w-5 h-5 text-category-produce-text" />
              <span>{t("broccoliCapitalized")}</span>
              <span className="text-sm text-muted-foreground ms-auto">{t("categoryProduce")}</span>
            </div>
            <div className="flex items-center gap-3 bg-category-dairy-bg rounded-lg p-3 border">
              <Milk className="w-5 h-5 text-category-dairy-text" />
              <span>{t("milkWithQuantity")}</span>
              <span className="text-sm text-muted-foreground ms-auto">{t("categoryDairy")}</span>
            </div>
          </div>
        ),
      },
      {
        title: t("swipeToCheck"),
        description: t("swipeToCheckDescription"),
        visual: (
          <div className="relative max-w-xs mx-auto">
            <div className="bg-card border rounded-lg p-4 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded border-2 border-muted-foreground/50" />
                  <span>{t("chickenBreastCapitalized")}</span>
                </div>
              </div>
            </div>
            <div className="text-sm text-center mt-3 mb-6 text-muted-foreground">
              {t("clickToMark")}
            </div>
            <div className="bg-[#22C55E] border rounded-lg shadow-sm overflow-hidden">
              <div className="flex items-center">
                <div className="relative w-20 h-full flex items-center">
                  <Archive className="text-white w-5 h-5 ms-4" />
                </div>
                <div className="bg-card flex-1 flex items-center justify-between gap-3 p-4">
                  <span>{t("chickenBreastCapitalized")}</span>
                  <ArrowRight className="w-5 h-5 text-[#22C55E] animate-pulse" />
                </div>
              </div>
            </div>
            <div className="text-sm text-center mt-3 text-muted-foreground">{t("swipeToMark")}</div>
          </div>
        ),
      },
      {
        title: t("multipleLists"),
        description: t("multipleListsDescription"),
        visual: (
          <div className="space-y-2">
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center">
                <span className="w-3 h-3 bg-primary rounded-full me-2" />
                <span>{t("groceryList1")}</span>
              </div>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-[#2ecc71] me-2" />
                <span>{t("groceryList2")}</span>
              </div>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center">
                <span className="w-3 h-3 rounded-full bg-[#e91e63] me-2" />
                <span>{t("groceryList3")}</span>
              </div>
            </div>
          </div>
        ),
      },
      {
        title: t("sharedLists"),
        description: t("sharedListsDescription"),
        visual: (
          <div className="space-y-3">
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 bg-primary rounded-full`} />
                  <span>{t("familyGroceryList")}</span>
                </div>
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div className="text-xs text-muted-foreground">
                {t("sharedWith", { emails: "mom@example.com, dad@example.com" })}
              </div>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#00b300]" />
                  <span>{t("friendGroceryList")}</span>
                </div>
                <Users className="w-4 h-4 text-primary" />
              </div>
              <div className="text-xs text-muted-foreground">
                {t("sharedWith", { emails: "friend1@example.com, friend2@example.com" })}
              </div>
            </div>
          </div>
        ),
      },
    ];

    return allSlides;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <TutorialSheet isOpen={isOpen} onClose={onClose} slides={slides} />;
}
